import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/switch/switch.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import {
  derivePeriodTag,
  getActiveIob,
  getRecentGlucose,
  loadUserSettings,
  saveFocusedEntry,
  saveManualGlucoseReading,
  REMEMBER_GL_WINDOW_MINUTES,
} from "../utils/entry-flow";
import {
  calculateCorrectionDose,
  calculatePFDoseWithCarbs,
  calculateQADoseForCarbs,
  PROTEIN_FAT_CONFIG,
} from "../config/dafne/insulin";
import { BG_THRESHOLDS } from "../config/dafne/thresholds";
import { DEFAULT_SETTINGS, PERIOD_TAG_INFO } from "../types";
import type { PeriodTag, UserSettings } from "../types";

const TARGET_GL = 6.0;

/**
 * Focused flow: a meal - carbs and the quick-acting insulin for it. The most
 * complex flow: carbs -> CP -> quick dose from the period ratio, an optional
 * correction from a remembered/entered glucose, and the optional Extra P/F
 * dose for high protein/fat meals. See docs/add-task-flows.md.
 */
@customElement("add-meal-page")
export class AddMealPage extends LitElement {
  @state() private carbs = "";
  @state() private gl = "";
  @state() private glFromMemory = false;
  @state() private recentAgeMinutes: number | null = null;
  @state() private qaBase = ""; // editable quick dose for carbs
  @state() private qaCorrection = ""; // editable correction
  @state() private pfEnabled = false;
  @state() private pfPercent: 20 | 30 = PROTEIN_FAT_CONFIG.STARTING_PERCENT;
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };
  @state() private period: PeriodTag = "B";
  @state() private correctionIob = 0;

  static styles = [
    flowStyles,
    css`
      .field {
        margin-bottom: 1.1rem;
      }
      .field-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }
      .cp-readout {
        margin-left: auto;
        font-weight: 600;
        color: #fd7e14;
      }
      .memory-note {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8rem;
        color: #0d6efd;
        background: #e7f1ff;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        margin-bottom: 0.75rem;
      }
      .pf-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
      }
      .pf-percent {
        margin-top: 0.75rem;
      }
      .summary {
        margin-top: 1rem;
        padding: 0.875rem 1rem;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.75rem;
      }
      .summary-row {
        display: flex;
        justify-content: space-between;
        font-size: 0.9rem;
        color: #495057;
        padding: 0.15rem 0;
      }
      .summary-total {
        font-weight: 700;
        color: #1a1a1a;
        border-top: 1px solid #dee2e6;
        margin-top: 0.4rem;
        padding-top: 0.5rem;
      }
      .small-input::part(base) {
        height: 44px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
    this.period = derivePeriodTag(new Date(), this.settings);
    const iob = await getActiveIob();
    this.correctionIob = iob.correction;

    const recent = await getRecentGlucose(REMEMBER_GL_WINDOW_MINUTES);
    if (recent) {
      this.gl = recent.glucose.toFixed(1);
      this.glFromMemory = true;
      this.recentAgeMinutes = recent.ageMinutes;
      // Pre-fill the correction from the remembered glucose so it matches the
      // shown suggestion; the user can still edit or clear it.
      const c = this._suggestedCorrection;
      if (c > 0) this.qaCorrection = c.toString();
    }
  }

  private get _ratioKey(): string {
    return PERIOD_TAG_INFO[this.period]?.ratioFrom ?? this.period;
  }

  private get _unitsPerCP(): number {
    return this.settings.cpRatios?.[this._ratioKey] ?? 0;
  }

  private get _cp(): number {
    const grams = parseFloat(this.carbs);
    if (isNaN(grams) || grams <= 0) return 0;
    return Math.round((grams / 10) * 10) / 10;
  }

  private get _doseIncrement(): number {
    return this.settings.doseIncrement || 0.5;
  }

  /** Suggested quick dose for the carbs from the period ratio. */
  private get _suggestedCarbDose(): number {
    if (this._cp <= 0 || !this._unitsPerCP) return 0;
    return calculateQADoseForCarbs(
      this._cp,
      this.settings.cpRatios,
      this._ratioKey,
      this._doseIncrement,
    );
  }

  /** Suggested correction from a usable glucose, net of active correction IOB. */
  private get _suggestedCorrection(): number {
    const gl = parseFloat(this.gl);
    const cf = this.settings.correctionFactor;
    if (isNaN(gl) || !cf || gl < BG_THRESHOLDS.HYPO) return 0;
    const net = calculateCorrectionDose(gl, TARGET_GL, cf, this.correctionIob);
    return net > 0.25
      ? Math.round(net / this._doseIncrement) * this._doseIncrement
      : 0;
  }

  /** P/F dose: 20%/30% of the suggested carb dose, rounded up. */
  private get _pfDose(): number {
    if (!this.pfEnabled) return 0;
    return calculatePFDoseWithCarbs(
      this._suggestedCarbDose,
      this.pfPercent,
      this._doseIncrement,
    );
  }

  private _onCarbsInput(value: string) {
    this.carbs = value;
    // Keep the quick dose in sync with the suggestion until the user edits it.
    this.qaBase = this._suggestedCarbDose
      ? this._suggestedCarbDose.toString()
      : "";
  }

  private _onGlInput(value: string) {
    this.gl = value;
    this.glFromMemory = false;
    this.recentAgeMinutes = null;
    const c = this._suggestedCorrection;
    this.qaCorrection = c ? c.toString() : "";
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const carbs = parseFloat(this.carbs);
    if (isNaN(carbs) || carbs <= 0 || this.isSaving) return;

    this.isSaving = true;
    try {
      const when = new Date();
      const qaBase = parseFloat(this.qaBase);
      const qaCorrection = parseFloat(this.qaCorrection);
      const gl = parseFloat(this.gl);
      const hasFreshGl = !this.glFromMemory && !isNaN(gl) && gl > 0;
      const pfDose = this._pfDose;

      const fields: Parameters<typeof saveFocusedEntry>[0] = {
        periodTag: this.period,
        carbs,
      };
      if (!isNaN(qaBase) && qaBase > 0) fields.qaBase = qaBase;
      if (!isNaN(qaCorrection) && qaCorrection !== 0) {
        fields.qaCorrection = qaCorrection;
      }
      if (pfDose > 0) {
        fields.pfBase = pfDose;
        fields.pfPercent = this.pfPercent;
      }
      if (this._unitsPerCP) fields.cpRatioUsed = this._unitsPerCP;
      if (hasFreshGl) {
        fields.bloodGlucose = gl;
        if (this.settings.correctionFactor) {
          fields.correctionFactorUsed = this.settings.correctionFactor;
        }
        await saveManualGlucoseReading(gl, when);
      }

      await saveFocusedEntry(fields, this.settings, when);
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save meal:", err);
      this.isSaving = false;
    }
  }

  private _renderRatioWarning() {
    if (this._cp <= 0 || this._unitsPerCP) return nothing;
    return html`
      <wa-callout variant="warning" open>
        <wa-icon name="circle-info" slot="icon"></wa-icon>
        No ${PERIOD_TAG_INFO[this.period]?.label ?? this.period} insulin-to-CP
        ratio set, so no quick dose can be suggested. Set it in Settings, or
        enter the dose manually.
      </wa-callout>
    `;
  }

  private _renderSummary() {
    const qaBase = parseFloat(this.qaBase);
    const qaCorrection = parseFloat(this.qaCorrection);
    const base = !isNaN(qaBase) && qaBase > 0 ? qaBase : 0;
    const corr = !isNaN(qaCorrection) ? qaCorrection : 0;
    const pf = this._pfDose;
    const total = base + corr + pf;
    if (total <= 0) return nothing;

    return html`
      <div class="summary">
        <div class="summary-row">
          <span>Quick for carbs</span><span>${base.toFixed(1)}u</span>
        </div>
        ${corr !== 0
          ? html`<div class="summary-row">
              <span>Correction</span
              ><span>${corr > 0 ? "+" : ""}${corr.toFixed(1)}u</span>
            </div>`
          : nothing}
        ${pf > 0
          ? html`<div class="summary-row">
              <span>Extra P/F (${this.pfPercent}%)</span
              ><span>${pf.toFixed(1)}u</span>
            </div>`
          : nothing}
        <div class="summary-row summary-total">
          <span>Total quick insulin</span><span>${total.toFixed(1)}u</span>
        </div>
      </div>
    `;
  }

  render() {
    const carbs = parseFloat(this.carbs);
    const canSave = !isNaN(carbs) && carbs > 0 && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Meal saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Meal</h1>
          <span class="header-badge"
            >${PERIOD_TAG_INFO[this.period]?.label ?? this.period}</span
          >
        </div>

        <div class="form-section">
          <div class="field">
            <div class="field-label">
              <wa-icon name="utensils"></wa-icon>
              Carbs
              ${this._cp > 0
                ? html`<span class="cp-readout">${this._cp} CP</span>`
                : nothing}
            </div>
            <wa-input
              class="flow-input"
              type="number"
              inputmode="numeric"
              min="0"
              max="500"
              step="1"
              placeholder="grams"
              .value=${this.carbs}
              @input=${(e: Event) =>
                this._onCarbsInput((e.target as HTMLInputElement).value)}
              autofocus
            ></wa-input>
            <div class="unit-hint">grams (1 CP = 10g)</div>
          </div>

          ${this._renderRatioWarning()}

          <div class="field">
            <div class="field-label">Quick insulin for carbs</div>
            <wa-input
              class="flow-input small-input"
              type="number"
              inputmode="decimal"
              min="0"
              max="100"
              step="0.5"
              placeholder="units"
              .value=${this.qaBase}
              @input=${(e: Event) =>
                (this.qaBase = (e.target as HTMLInputElement).value)}
            ></wa-input>
            <div class="unit-hint">units</div>
          </div>

          ${this.glFromMemory && this.recentAgeMinutes !== null
            ? html`
                <div class="memory-note">
                  <wa-icon name="clock-rotate-left"></wa-icon>
                  Glucose ${parseFloat(this.gl).toFixed(1)} mmol/L from
                  ${this.recentAgeMinutes} min ago - used for correction.
                </div>
              `
            : nothing}

          <div class="field">
            <div class="field-label">
              <wa-icon name="droplet"></wa-icon>
              Glucose now (optional)
            </div>
            <wa-input
              class="flow-input small-input"
              type="number"
              inputmode="decimal"
              min="1"
              max="33"
              step="0.1"
              placeholder="GL"
              .value=${this.gl}
              @input=${(e: Event) =>
                this._onGlInput((e.target as HTMLInputElement).value)}
            ></wa-input>
            <div class="unit-hint">mmol/L</div>
          </div>

          ${this._suggestedCorrection > 0 || parseFloat(this.qaCorrection)
            ? html`
                <div class="field">
                  <div class="field-label">Correction</div>
                  <wa-input
                    class="flow-input small-input"
                    type="number"
                    inputmode="decimal"
                    min="-20"
                    max="50"
                    step="0.5"
                    placeholder="units"
                    .value=${this.qaCorrection}
                    @input=${(e: Event) =>
                      (this.qaCorrection = (
                        e.target as HTMLInputElement
                      ).value)}
                  ></wa-input>
                  <div class="unit-hint">
                    suggested ${this._suggestedCorrection.toFixed(1)}u toward
                    ${TARGET_GL} mmol/L
                  </div>
                </div>
              `
            : nothing}

          <div class="field">
            <div class="pf-row">
              <div class="field-label" style="margin: 0;">
                <wa-icon name="drumstick-bite"></wa-icon>
                High protein/fat meal
              </div>
              <wa-switch
                ?checked=${this.pfEnabled}
                @wa-change=${(e: Event) =>
                  (this.pfEnabled = (e.target as HTMLInputElement).checked)}
              ></wa-switch>
            </div>
            ${this.pfEnabled
              ? html`
                  <div class="pf-percent chip-row">
                    ${(
                      [
                        PROTEIN_FAT_CONFIG.STARTING_PERCENT,
                        PROTEIN_FAT_CONFIG.ESCALATED_PERCENT,
                      ] as const
                    ).map(
                      (p) => html`
                        <button
                          class="chip ${this.pfPercent === p ? "selected" : ""}"
                          @click=${() => (this.pfPercent = p)}
                        >
                          ${p}%
                        </button>
                      `,
                    )}
                  </div>
                  <div
                    class="unit-hint"
                    style="text-align: left; margin-top: 0.5rem;"
                  >
                    ${this._pfDose > 0
                      ? html`Extra
                          <strong>${this._pfDose.toFixed(1)}u</strong> (${this
                            .pfPercent}%
                          of the carb dose), per OZDAFNE protein/fat dosing.`
                      : html`Enter carbs and a ratio to size the P/F dose.`}
                  </div>
                `
              : nothing}
          </div>

          ${this._renderSummary()}

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save meal"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-meal-page": AddMealPage;
  }
}
